import React, { useCallback, useState } from "react";

import dayjs from "dayjs";
import * as yup from "yup";
import { toast } from "react-toastify";
import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";

import Card from "react-bootstrap/Card";

import {
  QUERY_CURRENT_USER,
  QUERY_RECEPTIONIST_TEAMS_CONTACTS,
} from "../../config/graphql/query";
import { MUTATION_UPDATE_RECEPTIONIST_TEAMS_EMAIL } from "../../config/graphql/mutation";

interface IReceptionistTeamsContact {
  id: string;
  title?: string;
  email?: string;
  callMenuEnabled: boolean;
  updatedByName?: string;
  updatedByEmail?: string;
  updatedAt?: string;
}

const KNOWN_ERRORS = [
  "TEAMS_NOT_CONNECTED",
  "TEAMS_UNAVAILABLE",
  "TEAMS_USER_NOT_FOUND",
];

const emailSchema = yup.string().trim().email().required();

const ContactCard = React.memo(
  ({
    contact,
    myEmail,
  }: {
    contact: IReceptionistTeamsContact;
    myEmail?: string;
  }) => {
    const { t } = useTranslation(["receptionContact"]);

    const [email, setEmail] = useState(contact.email ?? "");

    const [onUpdate, { loading }] = useMutation(
      MUTATION_UPDATE_RECEPTIONIST_TEAMS_EMAIL,
    );

    const onSubmit = useCallback(
      (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!emailSchema.isValidSync(email)) {
          toast.error<string>(t("receptionContact:yup.email"));
          return;
        }

        onUpdate({
          variables: { input: { id: contact.id, email: email.trim() } },
        })
          .then(() => {
            toast.success<string>(t("receptionContact:toast.success"));
          })
          .catch((error: any) => {
            const code = error?.graphQLErrors?.[0]?.extensions?.code;

            toast.error<string>(
              KNOWN_ERRORS.includes(code)
                ? t(`receptionContact:errors.${code}`)
                : t("receptionContact:toast.error"),
            );
          });
      },
      [contact.id, email, onUpdate, t],
    );

    const date = contact.updatedAt
      ? dayjs(contact.updatedAt).format("DD.MM.YYYY HH:mm")
      : null;

    return (
      <Card className="col-xl-6 mb-3 p-3">
        <h5 className="mb-3">{contact.title}</h5>
        {contact.callMenuEnabled && (
          <div className="alert alert-warning">
            {t("receptionContact:callMenuWarning")}
          </div>
        )}
        <form onSubmit={onSubmit}>
          <div className="form-group">
            <label htmlFor={`email-${contact.id}`}>
              {t("receptionContact:email")}
            </label>
            <input
              id={`email-${contact.id}`}
              type="email"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="d-flex">
            {myEmail && (
              <button
                type="button"
                className="btn btn-secondary mr-2"
                onClick={() => setEmail(myEmail)}
              >
                {t("receptionContact:useMyEmail")}
              </button>
            )}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {t("receptionContact:save")}
            </button>
          </div>
        </form>
        {date && (
          <small className="text-muted mt-3">
            {contact.updatedByName
              ? t("receptionContact:changedBy", {
                  name: contact.updatedByName,
                  email: contact.updatedByEmail,
                  date,
                })
              : t("receptionContact:changedAt", { date })}
          </small>
        )}
      </Card>
    );
  },
);

const ReceptionContact: React.FC = () => {
  const { t } = useTranslation(["receptionContact"]);

  const { data: userData } = useQuery<{ me: IUser }>(QUERY_CURRENT_USER);

  const { data, loading } = useQuery<{
    receptionistTeamsContacts: IReceptionistTeamsContact[];
  }>(QUERY_RECEPTIONIST_TEAMS_CONTACTS, { fetchPolicy: "network-only" });

  const contacts = data?.receptionistTeamsContacts ?? [];

  return (
    <div className="container-fluid p-4">
      <h4 className="mb-2">{t("receptionContact:title")}</h4>
      <p className="text-muted mb-4">{t("receptionContact:intro")}</p>
      {!loading && contacts.length === 0 && (
        <p>{t("receptionContact:empty")}</p>
      )}
      {contacts.map((contact) => (
        <ContactCard
          key={contact.id}
          contact={contact}
          myEmail={userData?.me?.email}
        />
      ))}
    </div>
  );
};

export default ReceptionContact;
